import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../ui';
import { MobileSheet } from '../../ui/components/MobileSheet';
import { useCoffeeShops, useCoffeeTypes } from './hooks/useCoffeeShops';
import { ShopListView, ShopMapView, ShopRecommendationsView } from './views';
import RecommendSheet from './components/RecommendSheet';
import NearYouHero from './components/NearYouHero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faMap, faStar, faMugSaucer, faLink } from '@fortawesome/free-solid-svg-icons';
import { useUserLocation } from './hooks/useUserLocation';
import { useAppSelector } from '../../store/hooks';

const SHOP_TYPES = [
    { value: 'all', label: 'All' },
    { value: 'independent', label: 'Independent' },
    { value: 'coffee-chain', label: 'Chains' },
    { value: 'roastery', label: 'Roasteries' },
    { value: 'bakery-cafe', label: 'Bakery & Cafe' }
];

type ViewKey = 'list' | 'map' | 'foryou';

const VIEW_OPTIONS: { value: ViewKey; label: string; icon: typeof faList; mobile: boolean }[] = [
    { value: 'list',   label: 'List',    icon: faList, mobile: true },
    { value: 'map',    label: 'Map',     icon: faMap,  mobile: false },
    { value: 'foryou', label: 'For You', icon: faStar, mobile: true }
];

const CoffeeShopsPage: React.FC = () => {
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.auth);
    const [shopTypeFilter, setShopTypeFilter] = useState<string>('all');
    const [view, setView] = useState<ViewKey>('list');
    const [mapSheetOpen, setMapSheetOpen] = useState(false);
    const [recommendSheetOpen, setRecommendSheetOpen] = useState(false);

    const { coffeeShops, loading, error, refetch } = useCoffeeShops({ shopType: shopTypeFilter });
    const { coffeeTypes } = useCoffeeTypes();
    const { location } = useUserLocation(false);

    const coffeeTypesCache = useMemo(() => {
        const cache: Record<string, { iconName: string; displayName: string }> = {};
        coffeeTypes.forEach(ct => {
            cache[ct.coffeeKey] = { iconName: ct.iconName, displayName: ct.displayName };
        });
        return cache;
    }, [coffeeTypes]);

    // single-source-of-truth stats — only count rated shops
    const stats = useMemo(() => {
        const rated = coffeeShops.filter(s => s.totalRatings > 0);
        const totalRatings = rated.reduce((sum, s) => sum + s.totalRatings, 0);
        let nearMe = 0;
        if (location) {
            // approximate "5 km" via simple bbox so we don't import Haversine here
            const dLat = 0.045; // ~5 km
            const dLng = 0.06;
            nearMe = rated.filter(s =>
                Math.abs(s.coordinates.lat - location.lat) < dLat &&
                Math.abs(s.coordinates.lng - location.lng) < dLng
            ).length;
        }
        return { shops: rated.length, ratings: totalRatings, nearMe };
    }, [coffeeShops, location]);

    const handleShopClick = (shopId: string) => {
        setMapSheetOpen(false);
        setRecommendSheetOpen(false);
        navigate(`/coffeeshops/${shopId}`);
    };

    const handleViewChange = (next: ViewKey) => {
        // mobile has no map tab — open the sheet instead
        if (next === 'map' && !window.matchMedia('(min-width: 640px)').matches) {
            setMapSheetOpen(true);
            return;
        }
        setView(next);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (error) {
        return (
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="bg-paper border-brutal border-ink shadow-brutal-sm rounded-sm p-10 text-center">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-pink mb-2">Error</h2>
                    <p className="text-ink/65 mb-4">{error}</p>
                    <Button onClick={refetch} variant="soft">Try Again</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
            {/* ── HEADER ─────────────────────────────────────────────── */}
            <header className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl sm:text-5xl font-black text-ink uppercase tracking-tight leading-none">
                        Coffee shops
                    </h1>
                    <p className="text-ink/65 text-sm sm:text-base mt-2 max-w-md">
                        Community-rated cafés, espresso bars and roasteries.
                    </p>
                </div>
                {/* inline stat strip — flat, mono, no candy chrome */}
                <div className="flex items-baseline gap-3 text-[11px] font-black uppercase tracking-[0.12em] text-ink/55 sm:text-right">
                    <Stat n={stats.shops} label="shops" />
                    <span className="text-ink/20">│</span>
                    <Stat n={stats.ratings} label="ratings" />
                    {location && (
                        <>
                            <span className="text-ink/20">│</span>
                            <Stat n={stats.nearMe} label="near you" highlight />
                        </>
                    )}
                </div>
            </header>

            {/* ── NEAR YOU HERO ──────────────────────────────────────── */}
            {!loading && (
                <NearYouHero
                    coffeeShops={coffeeShops}
                    coffeeTypesCache={coffeeTypesCache}
                    onOpenMap={() => handleViewChange('map')}
                />
            )}

            {/* ── CONTROLS STRIP ─────────────────────────────────────── */}
            <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                {/* type chips */}
                <div className="flex flex-wrap gap-2">
                    {SHOP_TYPES.map((t) => {
                        const active = shopTypeFilter === t.value;
                        return (
                            <button
                                key={t.value}
                                type="button"
                                onClick={() => setShopTypeFilter(t.value)}
                                className={`px-3 py-1.5 border-brutal border-ink rounded-full text-[10px] font-black uppercase tracking-[0.1em] transition-all ${
                                    active
                                        ? 'bg-ink text-paper'
                                        : 'bg-paper text-ink hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-sm shadow-brutal-sm'
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* view segmented + recommend — single border/shadow so offsets stay square */}
                <ViewSegment
                    value={view}
                    onChange={handleViewChange}
                    onRecommend={() => setRecommendSheetOpen(true)}
                />
            </div>

            {/* mobile bottom slots: Recommend + Map */}
            <Button
                onClick={() => setRecommendSheetOpen(true)}
                variant="softPrimary"
                icon={faMugSaucer}
                mobileSlot="special1"
                showText
                className="rounded-xl hidden"
            >
                Recommend
            </Button>
            <Button
                onClick={() => setMapSheetOpen(true)}
                variant="soft"
                icon={faMap}
                mobileSlot="special2"
                className="rounded-xl hidden"
            />

            {/* admin link, kept low-key */}
            {user?.role === 'admin' && (
                <div className="mb-3">
                    <Link
                        to="/admin/chains"
                        className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.12em] text-ink/55 hover:text-ink"
                    >
                        <FontAwesomeIcon icon={faLink} className="text-[10px]" /> Chain admin
                    </Link>
                </div>
            )}

            {/* ── ACTIVE VIEW ────────────────────────────────────────── */}
            <div>
                {view === 'list' && (
                    <ShopListView
                        coffeeShops={coffeeShops}
                        coffeeTypesCache={coffeeTypesCache}
                        loading={loading}
                    />
                )}
                {view === 'map' && (
                    <div className="hidden sm:block">
                        <ShopMapView onShopSelect={handleShopClick} shopTypeFilter={shopTypeFilter} />
                    </div>
                )}
                {view === 'foryou' && (
                    <ShopRecommendationsView onShopClick={handleShopClick} />
                )}
            </div>

            {/* sheets */}
            <MobileSheet
                open={mapSheetOpen}
                onOpenChange={setMapSheetOpen}
                maxHeight="80vh"
                disableDrag={false}
                showGrabber
                title="Coffee Shop Map"
            >
                <div className="p-4">
                    <ShopMapView onShopSelect={handleShopClick} shopTypeFilter={shopTypeFilter} />
                </div>
            </MobileSheet>
            <RecommendSheet
                open={recommendSheetOpen}
                onOpenChange={setRecommendSheetOpen}
                onShopClick={handleShopClick}
                coffeeTypesCache={coffeeTypesCache}
            />
        </div>
    );
};

// ── HELPERS ──────────────────────────────────────────────────────────

const Stat: React.FC<{ n: number; label: string; highlight?: boolean }> = ({ n, label, highlight }) => (
    <span className="inline-flex items-baseline gap-1.5">
        <span
            className={`tabular-nums font-black ${highlight ? 'text-ink bg-mint px-1.5 py-px' : 'text-ink'}`}
        >
            {n}
        </span>
        <span>{label}</span>
    </span>
);

interface ViewSegmentProps {
    value: ViewKey;
    onChange: (next: ViewKey) => void;
    onRecommend?: () => void;
}

const segmentBtn =
    'm-0 min-w-0 shrink-0 border-0 shadow-none outline-none rounded-none px-3 sm:px-4 py-2 inline-flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-[0.1em] transition-colors focus-visible:relative focus-visible:z-[1] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink/35';

const ViewSegment: React.FC<ViewSegmentProps> = ({ value, onChange, onRecommend }) => {
    return (
        <div className="inline-flex items-stretch divide-x divide-ink rounded-full border-brutal border-ink bg-paper shadow-brutal-sm overflow-hidden">
            {VIEW_OPTIONS.map((opt) => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`${segmentBtn} ${
                            active ? 'bg-ink text-paper' : 'bg-paper text-ink hover:bg-ink/5'
                        } ${!opt.mobile ? 'hidden sm:inline-flex' : ''}`}
                        aria-pressed={active}
                    >
                        <FontAwesomeIcon icon={opt.icon} className="text-xs" />
                        <span>{opt.label}</span>
                    </button>
                );
            })}
            {onRecommend ? (
                <button
                    type="button"
                    onClick={onRecommend}
                    className={`${segmentBtn} hidden sm:inline-flex bg-paper text-ink hover:bg-ink/5`}
                    title="Quick recommend near me"
                >
                    <FontAwesomeIcon icon={faMugSaucer} className="text-xs" />
                    <span>Recommend</span>
                </button>
            ) : null}
        </div>
    );
};

export default CoffeeShopsPage;
